import { useQuery } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';

export interface CategoryItem {
  categoryId: string;
  categoryCode: string;
  categoryName: string;
  parentCategoryId: string | null;
  /** 관리자 목록에서만 온다 — false 면 숨김(비활성) 카테고리. 공개 목록은 활성만이라 없음(=활성). */
  active?: boolean;
}

interface CategoriesEnvelope {
  success: boolean;
  data?: { categories: CategoryItem[] } | null;
}

const fetchCategories = async (): Promise<CategoryItem[]> => {
  // 공개 엔드포인트 — 인증 불필요하므로 adminFetch(인증·401 갱신)를 쓰지 않는다.
  const res = await fetch('/api/v1/catalog/categories?flat=true');
  if (!res.ok) throw new Error(`categories fetch failed: ${res.status}`);
  const env = (await res.json()) as CategoriesEnvelope;
  return env.data?.categories ?? [];
};

export const categoriesQueryKey = () => ['public', 'categories'] as const;

export const useCategoriesQuery = (options: { enabled?: boolean } = {}) =>
  useQuery<CategoryItem[]>({
    queryKey: categoriesQueryKey(),
    queryFn: fetchCategories,
    staleTime: 5 * 60 * 1000,
    enabled: options.enabled ?? true,
  });

interface AdminFlatCategory {
  categoryId: string;
  parentCategoryId: string | null;
  categoryCode: string;
  name: string;
  active?: boolean;
}

/**
 * 상품 등록·수정 화면용 카테고리 목록 — 숨김(비활성) 카테고리까지.
 *
 * 공개 목록(`/api/v1/catalog/categories`)은 고객 화면용이라 활성만 준다. 그래서 숨김 카테고리에 연결된 상품
 * (예: QA 1000025079 → PRODUCT > china, 숨김 카테고리 6개·약 6,000건)은 카테고리 이름이 "(이름 없는 카테고리)"로
 * 보였다. 관리자 목록 `GET /admin/v1/categories?flat=true`(깊이 → 정렬순서)를 읽는다.
 * 관리자 카테고리 API 권한은 MD_MANAGER·OPS_ADMIN·SUPER_ADMIN 이라, 상품 조회만 되는 AUDITOR 는 403 —
 * 이때는 공개 목록으로 대신 읽는다(숨김 카테고리 이름은 비어 보인다).
 */
const fetchAdminCategoryItems = async (): Promise<CategoryItem[]> => {
  const res = await adminFetch('/admin/v1/categories?flat=true');
  if (res.status === 403) return fetchCategories();
  if (!res.ok) throw new Error(`admin categories fetch failed: ${res.status}`);
  const env = (await res.json()) as { data?: AdminFlatCategory[] | null };
  return (env.data ?? []).map((c) => ({
    categoryId: c.categoryId,
    categoryCode: c.categoryCode,
    categoryName: c.name,
    parentCategoryId: c.parentCategoryId,
    active: c.active !== false,
  }));
};

// ['admin', 'categories'] 접두 — 카테고리 관리 화면의 변경 무효화가 이 목록도 새로 읽게 한다.
export const adminCategoryItemsQueryKey = () => ['admin', 'categories', 'flat-items'] as const;

export const useAdminCategoryItemsQuery = (options: { enabled?: boolean } = {}) =>
  useQuery<CategoryItem[]>({
    queryKey: adminCategoryItemsQueryKey(),
    queryFn: fetchAdminCategoryItems,
    staleTime: 60 * 1000,
    enabled: options.enabled ?? true,
  });

/** 숨김 카테고리 이름 표시 — "china (숨김)". */
export const categoryDisplayName = (c: CategoryItem): string =>
  c.active === false ? `${c.categoryName} (숨김)` : c.categoryName;
